import { datadogRum, type RumEvent } from "@datadog/browser-rum";
import { type ShowcaseToken } from "@mg/schemas/src/prince/auth";
import { jwtDecode } from "jwt-decode";
import { useEffect } from "react";

import { DD_CLIENT_TOKEN, DD_HOST, DD_APP_ID, DD_VERSION } from "./env";

// why: React 18's production error logger runs console.error(<thrown value>) for every error a route boundary catches, and smartFetch throws the raw Response (utils/http.ts). Datadog serialises a non-Error, non-string console argument from Object.prototype.toString, so that RUM event is the bare placeholder `"[Response]"` — no error stack, only the handling stack of React's logger call site, no status, no URL — and a single one pages the prod RUM monitor.
const SERIALIZED_TYPE_PLACEHOLDER = /^"\[[A-Za-z][A-Za-z0-9]*\]"$/;

/**
 * A `console`-sourced RUM error whose logged value carried nothing readable: not
 * a string, not an Error, so Datadog replaced it with a type placeholder. Console
 * errors that carry a real message or an error stack are kept — this drops only
 * the events an engineer cannot act on.
 */
export function isUnreadableConsoleRumError(event: RumEvent): boolean {
  if (event.type !== "error") return false;

  const { message, source, stack } = event.error;
  // A stack means an Error reached console.error, so the event is readable.
  if (source !== "console" || stack != null) return false;

  return SERIALIZED_TYPE_PLACEHOLDER.test(message);
}

export function useDatadog() {
  useEffect(() => {
    if (DD_CLIENT_TOKEN && DD_APP_ID) {
      datadogRum.init({
        applicationId: DD_APP_ID,
        clientToken: DD_CLIENT_TOKEN,
        site: "datadoghq.com",
        service: "CARRIE",
        env: DD_HOST,
        version: DD_VERSION,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask-user-input",
        enableExperimentalFeatures: ["clickmap"],
        allowedTracingUrls: [
          (url) =>
            !!url.match(
              /^(https?:\/\/)?([a-zA-Z0-9-]+\.)*puntt\.ai(\/[^\s?]*)(\?[^\s]*)?$/,
            ),
        ],
        beforeSend: (event) => !isUnreadableConsoleRumError(event),
      });

      datadogRum.startSessionReplayRecording();

      // why: storage reads throw SecurityError rather than returning null when
      // storage is blocked (private mode, third-party-cookie blocking). This runs
      // in the RUM-init effect, so an unguarded read aborts app boot instead of
      // merely skipping user identification.
      let localToken: string | null = null;
      try {
        localToken = localStorage.getItem("token");
      } catch {
        localToken = null;
      }

      if (localToken) {
        try {
          const decodedToken = jwtDecode<ShowcaseToken>(localToken);
          datadogRum.setUser({
            id: decodedToken.userID,
            ...decodedToken,
          });
        } catch {
          // Corrupt/expired token — skip RUM user identification
        }
      }
    }
  }, []);

  return null;
}
